import type { Data } from '@urql/exchange-graphcache';
import { cacheExchange as cacheExchangeURQL } from '@urql/exchange-graphcache';

import schema from '../../generated/graphql.schema';
import apiClientRoutes from './apiClientRoutes';
import routesExchange from './routesCache';
import agencyHoursExchange from './agencyHoursCache';
import contractMutations from './contract/contractMutations';
import noteExchange from './noteCache';
import accountNoteExchange from './accountNoteCache';
import abTest from './school/abTest';
import refCodeTerm from './school/refCodeTerm';
import rentersImplementations from './school/rentersImplementation';
import tuitionImplementation from './school/tuitionImplementation';
import tuitionImportImplementation from './school/tuitionImportImplementation';
import refCodesMutations from './school/refCode';
import schoolMutations from './school/school';
import agentMutations from './agent/agentMutations';
import pressMutations from './press/pressMutations';
import trackingCodeMutations from './trackingCode/trackingCodeMutations';
import moratoriumMutations from './moratorium/moratoriumMutations';
import templateMutations from './template/templateMutations';
import offerContentMutations from './offerContent/offerContentMutations';
import auditQueryMutations from './auditQuery/auditQueryMutations';
import portalPartnerMutations from './portalPartner/portalPartner';
import skypadPolicyEditions from './skypadPolicy/skypadPolicyEditions';
import rentersInterestedParty from './skypad/rentersInterestedParty';
import quickUserV2Mutations from './quickUserV2/quickUserV2Mutations';
import interestedPartyMutations from './kamino/interestedParty';
import fileAiPromptMutations from './fileAiPrompt/fileAiPromptMutations';
import fileContentServiceMutations from './fileContentService/fileContentServiceMutations';

import eligibilityBypassMutations from '@/graphql/config/cache/EligibilityBypass/eligibilityBypassMutations';
import clientGroupMutations from '@/graphql/config/cache/clientGroup/clientGroup';
import refCodeClientGroupMutations from '@/graphql/config/cache/refCodeClientGroup/refCodeClientGroup';
import termTypeMutations from '@/graphql/config/cache/termType/termType';
import refundPolicyDeactivateExchange from '@/graphql/config/cache/RefundPolicyDeactivate';
import tuitionOfferDecisionCache from '@/graphql/config/cache/tuitionOfferDecision/tuitionOfferDecisionCache';
import refundPolicyDecisionExchange from '@/graphql/config/cache/refundPolicyDecision/refundPolicyDecisionCache';
import type { KaminoCancellationReasonCodeFragmentFragment, KaminoFnolFailedFragmentFragment, KaminoFnolFragmentFragment } from '@/graphql/generated/graphql';

const keyFromUuid = (data: Data) => data.uuid as string;

const cacheExchange = cacheExchangeURQL({
  schema,
  updates: {
    Mutation: {
      ...abTest,
      ...agencyHoursExchange,
      ...agentMutations,
      ...apiClientRoutes,
      ...auditQueryMutations,
      ...clientGroupMutations,
      ...contractMutations,
      ...eligibilityBypassMutations,
      ...moratoriumMutations,
      ...noteExchange,
      ...accountNoteExchange,
      ...offerContentMutations,
      ...portalPartnerMutations,
      ...pressMutations,
      ...refCodeClientGroupMutations,
      ...refCodeTerm,
      ...refCodesMutations,
      ...refundPolicyDeactivateExchange,
      ...refundPolicyDecisionExchange,
      ...rentersImplementations,
      ...routesExchange,
      ...schoolMutations,
      ...templateMutations,
      ...termTypeMutations,
      ...skypadPolicyEditions,
      ...trackingCodeMutations,
      ...tuitionImplementation,
      ...tuitionImportImplementation,
      ...rentersInterestedParty,
      ...tuitionOfferDecisionCache,
      ...quickUserV2Mutations,
      ...interestedPartyMutations,
      ...fileAiPromptMutations,
      ...fileContentServiceMutations,
    },
  },
  keys: {
    ABTestPaginator: () => null,
    APIClientPaginator: () => null,
    ActivityLogPaginator: () => null,
    AgencyHoursPaginator: () => null,
    AgentPaginator: () => null,
    AgentPermission: () => null,
    AgentRole: () => null,
    AuditQueryPaginator: () => null,
    ClientGroupPaginator: () => null,
    ColorRGB: () => null,
    ContractPaginator: () => null,
    EligibilityBypassPaginator: () => null,
    ErpPaginator: () => null,
    KaminoAccountHolder: keyFromUuid,
    KaminoAddress: keyFromUuid,
    KaminoDates: () => null,
    KaminoInsured: keyFromUuid,
    KaminoInsuredPerson: keyFromUuid,
    KaminoPaymentMethod: keyFromUuid,
    KaminoPaymentProfile: keyFromUuid,
    KaminoPerson: keyFromUuid,
    KaminoPolicy: keyFromUuid,
    KaminoPolicyCoverages: () => null,
    KaminoPolicyTransaction: keyFromUuid,
    KaminoPreCancelResponse: () => null,
    MembershipDuplicate: (data) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const membership = data as any;
      return membership.policy_number;
    },
    MoratoriumPaginator: () => null,
    NotePaginator: () => null,
    OfferContentPaginator: () => null,
    PartnerColors: () => null,
    PartnerPaginator: () => null,
    PartnerRightPaginator: () => null,
    PartnerRolePaginator: () => null,
    PaymentLinkPaginator: () => null,
    PermissionPaginator: () => null,
    PlanPaginator: () => null,
    PortalContactPaginator: () => null,
    PressPaginator: () => null,
    PropertyManagerPaginator: () => null,
    PropertyPaginator: () => null,
    QuickUserV2Paginator: () => null,
    RefCodeClientGroupPaginator: () => null,
    RefCodePaginator: () => null,
    RefCodeTermPaginator: () => null,
    RentManagerTenantsOutput: (data) => {
      if (data.contactId !== null && data.contactId !== undefined) {
        return data.contactId.toString();
      }
      return null;
    },
    RentersCoverageConfirmationPaginator: () => null,
    RentersImplementationPaginator: () => null,
    RentersPortalDecisionPaginator: () => null,
    RentersQuote: () => null,
    RentersQuoteAdminFee: () => null,
    RentersQuotePayment: () => null,
    RentersQuotePayments: () => null,
    RentersQuoteSurcharge: () => null,
    ReportCommandPaginator: () => null,
    RolePaginator: () => null,
    RoutePaginator: () => null,
    SchoolPaginator: () => null,
    StripePaymentMethod: (data: Data) => data?.id?.toString() ?? null,
    StripePaymentMethodBillingDetailsNameAndAddress: () => null,
    StripePaymentMethodCard: () => null,
    TemplateSetPaginator: () => null,
    TemplateTokenPaginator: () => null,
    TemplateTokenValuePaginator: () => null,
    TermTypePaginator: () => null,
    TermTypeRefCodePaginator: () => null,
    TrackingCodePaginator: () => null,
    TransactionalSmsPaginator: () => null,
    TuitionImplementationPaginator: () => null,
    TuitionImportImplementation: keyFromUuid,
    TuitionImportImplementationPaginator: () => null,
    TuitionQuote: () => null,
    UserPaginator: () => null,
    WebStartPaginator: () => null,
    KaminoFnolPaginator: () => null,
    KaminoReportableUnitPaginator: () => null,
    KaminoReportableUnit: keyFromUuid,
    KaminoFnol: (data: Data) => (data as KaminoFnolFragmentFragment).uuid,
    KaminoFnolFailed: (data: Data) => (data as KaminoFnolFailedFragmentFragment).uuid,
    PaginatorInfo: () => null,
    KaminoCancellationReasonCode: (data: Data) => (
      data as KaminoCancellationReasonCodeFragmentFragment
    ).code,
    FilePaginator: () => null,
    FileContentPaginator: () => null,
    FileAiPromptPaginator: () => null,
    FileAiReviewPaginator: () => null,
    FileContentServicePaginator: () => null,
  },
});

export default cacheExchange;
